import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTimesheetApproval } from './useTimesheetApproval';
import TimesheetApprovalContent from './TimesheetApprovalContent';
import RefreshWarningModal from './RefreshWarningModal';
import './approval.style.scss';

const REFRESH_REMINDER_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

const TimesheetApprovalPageWithoutLogin: React.FC = () => {
    const location = useLocation();
    const [token, setToken] = useState<string>('');
    const [notification, setNotification] = useState<{
        show: boolean;
        message: string;
        type: 'success' | 'error';
    }>({ show: false, message: '', type: 'success' });

    // Auto-refresh state
    const [showRefreshModal, setShowRefreshModal] = useState(false);
    const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

    const showNotificationMessage = (message: string, type: 'success' | 'error') => {
        setNotification({ show: true, message, type });
        const timeout = type === 'success' ? 3000 : 5000;
        setTimeout(() => {
            setNotification({ show: false, message: '', type });
        }, timeout);
    };

    const {
        pageState,
        timesheets,
        clientName,
        errorType,
        errorMessage,
        loadTimesheets,
        handleApprove,
        handleReject,
    } = useTimesheetApproval({
        token,
        onApproveSuccess: count => {
            const message =
                count === 1
                    ? 'Timesheet approved successfully'
                    : `${count} timesheets approved successfully`;
            showNotificationMessage(message, 'success');
        },
        onApproveError: message => {
            showNotificationMessage(message, 'error');
        },
        onRejectSuccess: () => {
            showNotificationMessage('Timesheet rejected successfully', 'success');
        },
        onRejectError: message => {
            showNotificationMessage(message, 'error');
        },
    });

    const clearRefreshTimer = () => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
    };

    const startRefreshTimer = () => {
        clearRefreshTimer();

        refreshTimerRef.current = setTimeout(() => {
            setShowRefreshModal(prevState => {
                if (!prevState) {
                    return true;
                }
                return prevState;
            });
        }, REFRESH_REMINDER_TIME);
    };

    const handleDismissRefreshModal = () => {
        setShowRefreshModal(false);
        startRefreshTimer();
    };

    const handleRefreshNow = () => {
        window.location.reload();
    };

    // Extract token from URL and load timesheets on mount
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tokenFromUrl = searchParams.get('token');

        if (tokenFromUrl) {
            setToken(tokenFromUrl);
            // loadTimesheets will be called in the next useEffect when token is set
        }

        return () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load timesheets when token is available
    useEffect(() => {
        if (token) {
            loadTimesheets();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Start refresh timer when page is successfully loaded
    useEffect(() => {
        if (pageState === 'loaded') {
            startRefreshTimer();
        }
        return () => {
            clearRefreshTimer();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageState]);

    // Show error if no token in URL
    if (!token && pageState === 'loading') {
        return (
            <div className="operations-page console">
                <div className="operations-page__inner">
                    <div className="operations-page__row">
                        <div className="operations-page__aside-column">
                            <div className="operations-page__aside-content">
                                <div className="operations-page__logo">
                                    <img
                                        alt="Logo white"
                                        src="/static/images/logo-white.png"
                                        width="130"
                                        height="40"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="operations-page__content-column">
                            <TimesheetApprovalContent
                                pageState="error"
                                timesheets={[]}
                                errorType="invalid"
                                errorMessage=""
                                showErrorPage={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="operations-page console">
            {notification.show && (
                <div className={`approval-notification approval-notification--${notification.type}`}>
                    <span className="approval-notification__message">{notification.message}</span>
                    <button
                        type="button"
                        className="approval-notification__close"
                        onClick={() => setNotification({ show: false, message: '', type: 'success' })}
                    >
                        ×
                    </button>
                </div>
            )}

            <RefreshWarningModal
                isOpen={showRefreshModal}
                onDismiss={handleDismissRefreshModal}
                onRefresh={handleRefreshNow}
                reminderMinutes={REFRESH_REMINDER_TIME / 60 / 1000}
            />

            <div className="operations-page__inner">
                <div className="operations-page__row">
                    <div className="operations-page__aside-column">
                        <div className="operations-page__aside-content">
                            <div className="operations-page__logo">
                                <img
                                    alt="Logo white"
                                    src="/static/images/logo-white.png"
                                    width="130"
                                    height="40"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="operations-page__content-column">
                        <TimesheetApprovalContent
                            pageState={pageState}
                            timesheets={timesheets}
                            clientName={clientName}
                            errorType={errorType}
                            errorMessage={errorMessage}
                            onApprove={handleApprove}
                            onReject={handleReject}
                            onRetry={loadTimesheets}
                            showErrorPage={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TimesheetApprovalPageWithoutLogin;
